import React, { useState } from 'react';
import { Box, Chip, IconButton, InputBase, Tooltip } from '@mui/material';
import { FilterList } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { formatConditionChipLabel } from './filterEngine';

const ACCENT = 'var(--rd-primary)';

const TaskSearchBar = ({
  searchChips = [],
  onAddSearchChip,
  onRemoveSearchChip,
  conditions = [],
  onRemoveCondition,
  onOpenFilter,
  filterActive = false,
  endContent = null,
  afterFilterContent = null,
  formatConditionLabel,
  placeholder
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');

  const commitChip = () => {
    const value = inputValue.trim();
    if (!value) return;
    onAddSearchChip?.(value);
    setInputValue('');
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitChip();
      return;
    }
    if (event.key === 'Backspace' && !inputValue && searchChips.length > 0) {
      onRemoveSearchChip?.(searchChips[searchChips.length - 1]);
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
      <Box
        sx={{
          flex: 1,
          minHeight: 40,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 0.75,
          px: 1.25,
          py: 0.5,
          backgroundColor: 'transparent'
        }}
      >
        {searchChips.map((chip) => (
          <Chip
            key={`search-${chip}`}
            label={chip}
            size="small"
            onDelete={() => onRemoveSearchChip?.(chip)}
            sx={{
              backgroundColor: 'var(--rd-bg-subtle)',
              '& .MuiChip-deleteIcon': { fontSize: 16 }
            }}
          />
        ))}
        {conditions.map((condition) => (
          <Chip
            key={condition.id}
            label={formatConditionLabel
              ? formatConditionLabel(condition, t)
              : formatConditionChipLabel(condition, t)}
            size="small"
            onDelete={() => onRemoveCondition?.(condition.id)}
            sx={{
              backgroundColor: 'color-mix(in srgb, var(--rd-primary) 12%, transparent)',
              border: '1px solid color-mix(in srgb, var(--rd-primary) 35%, transparent)',
              '& .MuiChip-deleteIcon': { fontSize: 16 }
            }}
          />
        ))}
        <InputBase
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={commitChip}
          placeholder={placeholder || t('taskFilters.placeholder')}
          sx={{
            flex: 1,
            minWidth: 160,
            fontSize: 14,
            '& input::placeholder': {
              color: 'text.disabled',
              opacity: 1
            }
          }}
        />
      </Box>
      {endContent}
      <Tooltip title={t('taskFilters.openFilter')}>
        <IconButton
          onClick={(event) => onOpenFilter?.(event)}
          aria-label={t('taskFilters.openFilter')}
          sx={{
            color: filterActive ? ACCENT : 'text.secondary',
            border: filterActive ? `2px solid ${ACCENT}` : '2px solid transparent',
            '&:hover': { color: ACCENT }
          }}
        >
          <FilterList />
        </IconButton>
      </Tooltip>
      {afterFilterContent}
    </Box>
  );
};

export default TaskSearchBar;
